import React, { useEffect, useState } from 'react';
import AccountPopover from './AccountPopover';
import InfoTip from './InfoTip';
import ModalCloseButton from './ModalCloseButton';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { isLivePreviewEditorEnabled, setLivePreviewEditorEnabled } from '../lib/editorFlag';
import { formatSyncLog } from '../lib/syncLog';
import { formatContentTrail, isTrailEnabled, setTrailEnabled } from '../lib/contentTrail';
import { formatShrinkLog } from '../lib/shrinkLog';
import { formatFocusTrail } from '../lib/focusTrail';

// Walter 2026-05-09: copy from popup_help_inventory.md §1.3 / §1.4 —
// the two reminder-delivery rows. Kept here so the Row label can stay
// a small inline-flex flow instead of inlining markdown literals into
// the JSX.
const PUSH_TIP = "Browser notifications. Requires a one-time permission grant from your browser. Push only works on the browser/device you grant permission on — turn email on too if you want reminders across devices.";
const EMAIL_TIP = "Reminder emails are sent to the address you signed in with. Works on every device — no permission needed.";
const DIGEST_TIP = "Get an email summary of your reminders (overdue, today, undated, upcoming) at a time you pick. Additive — per-reminder emails still fire normally.";

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Convert 0–23 hour integer ↔ "HH:00" string for the time input.
function hourToStr(h) {
  const v = Number.isFinite(h) ? Math.max(0, Math.min(23, h)) : 7;
  return `${String(v).padStart(2, '0')}:00`;
}
function strToHour(s) {
  const m = (s || '').match(/^(\d{1,2}):/);
  if (!m) return 7;
  return Math.max(0, Math.min(23, parseInt(m[1], 10)));
}

// Font-size presets — stored value is the raw px number.
const FONT_SIZES = [
  { label: 'S', value: 11 },
  { label: 'M', value: 12 },
  { label: 'L', value: 14 },
  { label: 'XL', value: 16 },
];

// Line-height presets — stored value is the unitless CSS line-height.
const LINE_HEIGHTS = [
  { label: 'tight',   value: 1.3 },
  { label: 'normal',  value: 1.5 },
  { label: 'relaxed', value: 1.7 },
  { label: 'loose',   value: 1.9 },
];

/**
 * SettingsModal
 *
 * Small floating dialog for user preferences. Values are persisted to
 * localStorage by useSettings() (see hooks/useSettings.js), not here — we
 * just render controls and call the setters.
 */
export default function SettingsModal({
  open,
  onClose,
  theme,
  onThemeChange,
  palette,
  onPaletteChange,
  fontSize,
  onFontSizeChange,
  lineHeight,
  onLineHeightChange,
  // Walter 2026-06-06: the canvas layout mode (free vs grid) is a
  // per-board setting that previously only lived behind two small icons
  // in the top bar — users didn't realise it existed or what it did.
  // When these are provided (desktop board context), we surface the same
  // toggle here with a word label + a tip. boardMode is 'free' | 'grid';
  // onBoardModeChange(nextMode) flips the CURRENT board.
  boardMode,
  onBoardModeChange,
  // Optional: when provided, renders a sign-out button at the bottom.
  // Mobile relies on this (its header was too cramped to host sign-out);
  // desktop also gets the entry-point here alongside the inline header btn.
  onSignOut,
  // When true, renders an Account section at the top of the modal
  // showing identity / provider, or guest-conversion CTAs. Mobile uses
  // this to surface what desktop shows in its header AccountPopover.
  // Desktop omits this (the header popover already exposes the same
  // controls; duplicating them in Settings clutters the modal).
  showAccountSection = false,
  // When true AND showAccountSection is on, the account section starts
  // EXPANDED rather than collapsed. Used when Settings is opened via a
  // "sign in" call-to-action (e.g. the onboarding checklist on mobile)
  // so the sign-in options are right there, not behind a tap. Walter
  // 2026-06-01.
  expandAccountOnOpen = false,
  user,
  boards,
  lists,                // current board's lists — used to render the
                        // designated-reminder-list picker
  listsCount,
  userPrefs,            // Firestore-backed user-wide prefs
  updateUserPrefs,      // partial-update callback
  pushSub,              // optional: usePushSubscription return value;
                        // when provided, the push toggle becomes a
                        // real permission-request flow.
  currentBoardId,       // needed when persisting designated list (we
                        // store boardId alongside listId so we can
                        // navigate to the list later)
  onLinkGoogle,
  onLinkMagicLink,
}) {
  // Two-step sign-out — first tap swaps the button for a confirm/cancel pair
  // so the user can't blow themselves out of the session with a stray click.
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  // Walter 2026-05-13: account section at the top of mobile settings
  // is a collapsible row — default collapsed so the appearance + push
  // controls are visible without scrolling. Tap to expand.
  const [accountExpanded, setAccountExpanded] = useState(false);

  // Reset confirm state whenever the modal reopens. Account section
  // starts expanded only when the caller opened Settings specifically to
  // sign in (expandAccountOnOpen); otherwise collapsed so the appearance
  // controls are visible without scrolling.
  useEffect(() => {
    if (open) {
      setConfirmingSignOut(false);
      setAccountExpanded(!!expandAccountOnOpen);
    }
  }, [open, expandAccountOnOpen]);

  // Dismiss on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        // Full-viewport catcher so clicks outside the box still dismiss the
        // modal, but the backdrop itself is transparent — users need to see
        // the canvas behind the panel to judge colors while tweaking theme
        // and palette. The box's shadow does the heavy lifting for depth.
        position: 'fixed', inset: 0,
        background: 'transparent',
        zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'lists-fadein 0.15s ease',
        pointerEvents: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius)',
          minWidth: '320px',
          maxWidth: '460px',
          // Walter 2026-05-12: cap modal height + scroll the body so
          // mobile users with a tall content list (especially when the
          // account section is rendered up top) can reach everything,
          // and the close X stays anchored in the (non-scrolling)
          // header. 100dvh handles iOS Safari address-bar variance.
          maxHeight: 'calc(100dvh - 40px)',
          display: 'flex',
          flexDirection: 'column',
          // Padding moved to the inner header + body so the
          // sticky-feel header doesn't get cut off by box padding.
          padding: 0,
          boxShadow: 'var(--shadow-modal-flat)',
          fontFamily: 'var(--mono)',
        }}
      >
        {/* Header — non-scrolling so the X stays reachable at all
            scroll positions on mobile. */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 22px 14px',
          flexShrink: 0,
          borderBottom: '1px solid var(--border)',
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            fontSize: 'calc(13px * var(--ui-scale, 1))',
            fontWeight: 600, color: 'var(--text)',
          }}>
            <GearIcon />
            settings
          </div>
          <ModalCloseButton onClick={onClose} style={{ position: 'static' }} />
        </div>
        {/* Scrollable body wrapper — contains everything below the
            header. Walter 2026-05-12: overflowY here so the body
            scrolls independently of the (sticky-feel) header above. */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '18px 22px 20px',
          WebkitOverflowScrolling: 'touch',
        }}>

        {/* Account — only when caller asks for it (mobile, where there's
            no header AccountPopover). Walter 2026-05-13: renders as
            a collapsible row at the top so the appearance/delivery
            controls aren't pushed below the fold on first open.
            Default collapsed; tap the row to expand. */}
        {showAccountSection && user && (
          <div style={{
            marginBottom: '18px',
            borderBottom: '1px solid var(--border)',
            marginLeft: '-22px',
            marginRight: '-22px',
          }}>
            <button
              onClick={() => setAccountExpanded(v => !v)}
              style={{
                width: '100%',
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 22px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text)',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(12px * var(--ui-scale, 1))',
                textAlign: 'left',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round"
                style={{ color: 'var(--text-dim)' }}>
                <circle cx="12" cy="8" r="4"/>
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6"/>
              </svg>
              <span style={{ flex: 1, fontWeight: 600 }}>account</span>
              {/* Chevron — rotates to indicate expanded/collapsed. */}
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"
                style={{
                  color: 'var(--text-dimmer)',
                  transition: 'transform 0.15s',
                  transform: accountExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                }}>
                <path d="M3 1l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {accountExpanded && (
              <div style={{ paddingBottom: 10 }}>
                <AccountPopover
                  inline
                  user={user}
                  boards={boards || []}
                  listsCount={listsCount || 0}
                  onSignOut={() => { /* sign-out lives in the bottom row of this modal */ }}
                  onLinkGoogle={onLinkGoogle}
                  onLinkMagicLink={onLinkMagicLink}
                />
              </div>
            )}
          </div>
        )}

        <SectionHeader>appearance</SectionHeader>

        {/* Theme */}
        <Row label="theme">
          <Segmented
            options={[
              { label: 'dark', value: 'dark' },
              { label: 'light', value: 'light' },
            ]}
            value={theme}
            onChange={onThemeChange}
          />
        </Row>

        {/* Palette — "warm" is the orange-on-cream listicity look; "cold"
            restores the original green-on-black palette. Walter
            2026-05-06: renamed from default/tech for legibility.
            Internal palette IDs stay 'default'/'tech' so existing
            stored prefs keep working. */}
        {onPaletteChange && (
          <Row label="palette">
            <Segmented
              options={[
                { label: 'warm', value: 'default' },
                { label: 'cold', value: 'tech' },
              ]}
              value={palette}
              onChange={onPaletteChange}
            />
          </Row>
        )}

        {/* Font size */}
        <Row label="note font size">
          <Segmented
            options={FONT_SIZES.map(s => ({ label: s.label, value: s.value }))}
            value={fontSize}
            onChange={onFontSizeChange}
          />
        </Row>

        {/* Line height */}
        <Row label="line spacing">
          <Segmented
            options={LINE_HEIGHTS.map(s => ({ label: s.label, value: s.value }))}
            value={lineHeight}
            onChange={onLineHeightChange}
          />
        </Row>

        {/* Layout — mirrors the top-bar free/grid toggle so it's
            discoverable + labelled. Per-board (only shown in a board
            context, i.e. when boardMode + onBoardModeChange are passed).
            Walter 2026-06-06. */}
        {boardMode && onBoardModeChange && (
          <>
            <SectionHeader>layout</SectionHeader>
            <Row label={<RowLabelWithTip text="this board" tip="Free: drag tiles anywhere on the canvas. Grid: tiles snap into tidy columns. Affects the board you're viewing — each board remembers its own layout." />}>
              <Segmented
                options={[
                  { label: 'free', value: 'free' },
                  { label: 'grid', value: 'grid' },
                ]}
                value={boardMode}
                onChange={onBoardModeChange}
              />
            </Row>
          </>
        )}

        {/* Reminders — early beta. The first toggle inside the section
            controls whether the rest of the reminder controls render.
            We don't gate the whole section behind isRemindersEnabled
            anymore: users opt in via this toggle, then see the picker
            and the cross-list scan switch. The localStorage dev flag
            (lists_reminders_dev=1) still works for fast iteration. */}
        {userPrefs && updateUserPrefs && (
          <RemindersSection
            userPrefs={userPrefs}
            updateUserPrefs={updateUserPrefs}
            lists={lists || []}
            currentBoardId={currentBoardId}
            allBoards={boards || []}
            pushSub={pushSub}
          />
        )}

        {/* Install app — always-available entry point to install
            listicity as a PWA. Renders nothing when the user has
            nothing to do (already standalone, or browser doesn't
            support installation). Walter 2026-05-15: paired with the
            auto-banner so users who dismissed the banner can still
            find install here. */}
        <InstallSection />

        {/* Experimental — opt-in, device-local features. Walter 2026-07-19:
            un-gated to PROD ("go prod") after the CM6 mobile rounds (#141–145)
            — opt-in only, textarea stays the default; flipping the DEFAULT is
            a separate future decision. */}
        <ExperimentalSection />

        {/* Sharing & collaboration — alpha. Master toggle gates the
            entire feature: when off, the share buttons, "shared with
            me" section, and lock indicators were all hidden.
            Walter 2026-05-06 round 7: sharing is now always on; the
            section + toggle are removed. The userPrefs.
            sharingAlphaEnabled field stays in the schema for
            backward compatibility with old prefs docs but is no
            longer read anywhere. */}

        {/* Sign out — rendered only when the caller passes onSignOut AND
            the user isn't anonymous. Lives at the bottom of the modal so
            it's easy to reach with a thumb on mobile (where the header
            had no room for a dedicated button). Two-step: first tap
            reveals a confirm/cancel pair.
            Walter 2026-05-25 (Bug #5 — round-2 tester report): guests
            were seeing a "sign out" button that made no sense for an
            anonymous session. The Account section (when
            showAccountSection is on) already exposes "sign in with
            Google" / "link a magic-link email" for guests to upgrade.
            Walter 2026-06-02: gate on showAccountSection so this only
            shows where it's the SOLE sign-out — i.e. mobile (Settings IS
            the account surface). On desktop the header AccountPopover
            already has sign-out, so this one was redundant; hide it. */}
        {showAccountSection && onSignOut && !user?.isAnonymous && (
          <div style={{
            marginTop: '18px',
            paddingTop: '14px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '10px',
          }}>
            {confirmingSignOut ? (
              <>
                <span style={{
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(11px * var(--ui-scale, 1))',
                  color: 'var(--text-dim)',
                  flex: 1,
                }}>
                  sign out?
                </span>
                <button
                  onClick={() => setConfirmingSignOut(false)}
                  style={{
                    padding: '7px 14px',
                    background: 'transparent',
                    color: 'var(--text-dim)',
                    border: '1px solid var(--border-bright)',
                    borderRadius: 'var(--radius)',
                    fontFamily: 'var(--mono)',
                    fontSize: 'calc(11px * var(--ui-scale, 1))',
                    cursor: 'pointer',
                    transition: 'background 0.12s, color 0.12s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--surface2)';
                    e.currentTarget.style.color = 'var(--text)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--text-dim)';
                  }}
                >
                  cancel
                </button>
                <button
                  onClick={() => { onClose(); onSignOut(); }}
                  autoFocus
                  style={{
                    padding: '7px 14px',
                    background: 'var(--danger)',
                    color: 'var(--bg)',
                    border: '1px solid var(--danger)',
                    borderRadius: 'var(--radius)',
                    fontFamily: 'var(--mono)',
                    fontSize: 'calc(11px * var(--ui-scale, 1))',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'opacity 0.12s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  confirm
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirmingSignOut(true)}
                style={{
                  padding: '9px 18px',
                  background: 'transparent',
                  color: 'var(--danger)',
                  border: '1px solid var(--border-bright)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(12px * var(--ui-scale, 1))',
                  cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  transition: 'background 0.12s, border-color 0.12s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'var(--surface2)';
                  e.currentTarget.style.borderColor = 'var(--danger)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'var(--border-bright)';
                }}
              >
                <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                  <path d="M6 1H2a1 1 0 00-1 1v10a1 1 0 001 1h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                  <path d="M9 4l3 3-3 3M12 7H5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                sign out
              </button>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

// ————— helpers —————

/**
 * SectionHeader — upper-cased label that introduces a group of
 * related rows in the Settings modal (e.g. "appearance", "reminders").
 * The accent-tinted underline makes the section break legible at a
 * glance — quiet enough not to compete with the controls below it,
 * loud enough that you don't have to squint to spot section starts.
 */
// Walter 2026-06-06: cog glyph next to the "settings" title. (First pass
// drew a circle + 8 radial spokes, which read as a sun, not a gear —
// swapped for a proper toothed cog: Heroicons cog-6-tooth outline.)
function GearIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ display: 'block', flex: 'none', color: 'var(--accent)' }}>
      <path d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 0 1 0 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 0 1 0-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281Z" />
      <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );
}

// Experimental features — device-local opt-ins. Currently just the
// live-preview (CodeMirror) editor. Reads/writes localStorage directly (not
// Firestore prefs) and reloads so every open note re-renders with the choice.
function ExperimentalSection() {
  const [on, setOn] = useState(isLivePreviewEditorEnabled());
  const apply = (next) => {
    const enabled = next === 'on';
    setLivePreviewEditorEnabled(enabled);
    setOn(enabled);
    setTimeout(() => window.location.reload(), 120);
  };
  return (
    <>
      <SectionHeader>experimental</SectionHeader>
      <Row label={<RowLabelWithTip text="live-preview editor" tip="Beta editor: headings, bold and checkboxes render as you type (Obsidian-style). Works with the format menu and keyboard shortcuts. Per-device — doesn't sync to your other devices. Toggling reloads the page." />}>
        <Segmented
          options={[{ label: 'off', value: 'off' }, { label: 'on', value: 'on' }]}
          value={on ? 'on' : 'off'}
          onChange={apply}
        />
      </Row>
      <SyncLogRow />
      <ContentTrailRow />
      <ShrinkLogRow />
      <FocusTrailRow />
    </>
  );
}

/**
 * SyncLogRow — copy this device's sync log to the clipboard.
 *
 * Walter 2026-08-12: every sync investigation this month was me inferring
 * what happened from a screenshot taken hours later — twice from a MISSING
 * label. This makes it a paste instead. Records only sizes and short
 * fingerprints, never note content, and lives on the device (no Firestore
 * cost). Visible rather than dev-gated at his request, "until this is all
 * worked through".
 */
function SyncLogRow() {
  const [copied, setCopied] = useState(null);
  const copy = async () => {
    const text = formatSyncLog();
    try {
      await navigator.clipboard.writeText(text);
      setCopied('copied');
    } catch {
      // Clipboard denied (iOS without a user-gesture chain, http, etc.) —
      // fall back to the console so the log is still reachable.
      // eslint-disable-next-line no-console
      console.log(text);
      setCopied('see console');
    }
    setTimeout(() => setCopied(null), 2500);
  };
  return (
    <Row label={<RowLabelWithTip text="sync log" tip="A record of what syncing did on THIS device: saves that failed, edits waiting to reach the cloud, and how versions were merged. Sizes and fingerprints only — never the text of your notes. Copy it and paste it to Claude when something looks wrong." />}>
      <button
        type="button"
        onClick={copy}
        style={{
          background: 'transparent',
          color: 'var(--text-dim)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '3px 10px',
          cursor: 'pointer',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          whiteSpace: 'nowrap',
        }}
      >
        {copied || 'copy'}
      </button>
    </Row>
  );
}

/**
 * ContentTrailRow — the heavy diagnostic: keep the FULL TEXT of every version
 * this device saves or adopts, so a vanished sentence can be seen rather than
 * inferred.
 *
 * Walter 2026-08-31, after losing two small edits in a plain list in one
 * afternoon: *"maybe for diagnostics, you should just snapshot everything all
 * the time for a little while?"* The sync log records decisions, the list
 * history keeps daily snapshots — neither can show what the text said a minute
 * before the words went. This can.
 *
 * OFF by default and clearly temporary, because unlike the sync log it stores
 * real note text (on this device only, never uploaded). Turning it off wipes
 * what it collected.
 */
function ContentTrailRow() {
  const [on, setOn] = useState(isTrailEnabled());
  const [copied, setCopied] = useState(null);
  const toggle = (next) => { setTrailEnabled(next); setOn(next); };
  const copy = async () => {
    const text = formatContentTrail();
    try {
      await navigator.clipboard.writeText(text);
      setCopied('copied');
    } catch {
      // eslint-disable-next-line no-console
      console.log(text);
      setCopied('see console');
    }
    setTimeout(() => setCopied(null), 2500);
  };
  const btn = {
    background: 'transparent',
    color: 'var(--text-dim)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius)',
    padding: '3px 10px',
    cursor: 'pointer',
    fontFamily: 'var(--mono)',
    fontSize: 'calc(10px * var(--ui-scale, 1))',
    whiteSpace: 'nowrap',
  };
  return (
    <Row label={<RowLabelWithTip text="content trail (temporary)" tip="Keeps the full text of every version this device saves or receives, so a lost edit can be recovered and explained. Stays on this device — never uploaded. Unlike the sync log it DOES hold your note text, so leave it on only while we're chasing a problem; turning it off deletes what it collected." />}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button type="button" onClick={() => toggle(false)} style={{ ...btn, ...(on ? {} : { background: 'var(--accent)', color: 'var(--bg)', borderColor: 'var(--accent)' }) }}>off</button>
        <button type="button" onClick={() => toggle(true)} style={{ ...btn, ...(on ? { background: 'var(--accent)', color: 'var(--bg)', borderColor: 'var(--accent)' } : {}) }}>on</button>
        <button type="button" onClick={copy} style={btn}>{copied || 'copy'}</button>
      </div>
    </Row>
  );
}

/**
 * ShrinkLogRow — copy the record of incoming changes that REMOVED lines.
 *
 * Walter 2026-09-04: the content trail caught the bug but keeps only 60
 * versions, which he burns through in a few hours, while his losses are days
 * apart — *"it is often okay for several days in a row"*. This keeps only the
 * suspicious events (an adopt/merge that deleted lines, never his own edit),
 * so a couple of hundred of them span weeks. Always on, unlike the trail: it
 * costs nothing when nothing shrinks, and it has to be running BEFORE he
 * notices anything is wrong.
 */
function ShrinkLogRow() {
  const [copied, setCopied] = useState(null);
  const copy = async () => {
    const text = formatShrinkLog();
    try {
      await navigator.clipboard.writeText(text);
      setCopied('copied');
    } catch {
      // eslint-disable-next-line no-console
      console.log(text);
      setCopied('see console');
    }
    setTimeout(() => setCopied(null), 2500);
  };
  return (
    <Row label={<RowLabelWithTip text="lost-text log" tip="Records any time listicity RECEIVES a version of a list with lines missing — text you didn't delete yourself. Stays on this device, always on, and keeps far longer than the content trail. If something vanishes, copy this and send it." />}>
      <button
        type="button"
        onClick={copy}
        style={{
          background: 'transparent',
          color: 'var(--text-dim)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '3px 10px',
          cursor: 'pointer',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          whiteSpace: 'nowrap',
        }}
      >
        {copied || 'copy'}
      </button>
    </Row>
  );
}

/**
 * FocusTrailRow — copy the record of clicks that asked for a caret and didn't
 * get one.
 *
 * Walter 2026-11-03, twice in one day: *"the cursor wouldn't stick where I put
 * it in the to do list, instead it insisted on blinking in the reminders
 * list"*. Reading the code found no focus thief, and a refresh clears it, so
 * this records the next occurrence instead of us guessing at a third. Keeps
 * only the misses, so it spans weeks. See lib/focusTrail.js.
 */
function FocusTrailRow() {
  const [copied, setCopied] = useState(null);
  const copy = async () => {
    const text = formatFocusTrail();
    try {
      await navigator.clipboard.writeText(text);
      setCopied('copied');
    } catch {
      // eslint-disable-next-line no-console
      console.log(text);
      setCopied('see console');
    }
    setTimeout(() => setCopied(null), 2500);
  };
  return (
    <Row label={<RowLabelWithTip text="cursor log" tip="Records any time you click somewhere that should take the cursor and the cursor goes elsewhere — or moves on its own without you clicking or typing. Stays on this device, always on. Records which kind of field and which list, never the text. If the cursor misbehaves, copy this and send it." />}>
      <button
        type="button"
        onClick={copy}
        style={{
          background: 'transparent',
          color: 'var(--text-dim)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '3px 10px',
          cursor: 'pointer',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          whiteSpace: 'nowrap',
        }}
      >
        {copied || 'copy'}
      </button>
    </Row>
  );
}

function SectionHeader({ children }) {
  return (
    <div style={{
      marginTop: '6px',
      marginBottom: '12px',
      paddingBottom: '4px',
      borderBottom: '1px solid var(--accent-dim, var(--border-bright))',
      fontFamily: 'var(--mono)',
      fontSize: 'calc(11px * var(--ui-scale, 1))',
      fontWeight: 700,
      letterSpacing: '0.8px',
      textTransform: 'uppercase',
      color: 'var(--accent)',
    }}>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '16px', marginBottom: '14px',
    }}>
      <div style={{
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        color: 'var(--text-dim)',
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Segmented({ options, value, onChange }) {
  return (
    <div style={{
      display: 'inline-flex',
      border: '1px solid var(--border-bright)',
      borderRadius: 'var(--radius)',
      overflow: 'hidden',
    }}>
      {options.map((opt, i) => {
        const active = value === opt.value;
        return (
          <button
            key={String(opt.value)}
            onClick={() => onChange(opt.value)}
            style={{
              // Padding scaled with --ui-scale so XL font doesn't
              // overflow the button. Walter 2026-05-25 (Bug #6 —
              // round-2 tester report): at XL the "loose" line-spacing
              // label was truncated because the button kept its 5px/11px
              // fixed padding while the text grew ~1.33×.
              padding: 'calc(5px * var(--ui-scale, 1)) calc(11px * var(--ui-scale, 1))',
              background: active ? 'var(--accent)' : 'transparent',
              color: active ? 'var(--bg)' : 'var(--text-dim)',
              border: 'none',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              fontWeight: active ? 600 : 400,
              cursor: 'pointer',
              borderLeft: i === 0 ? 'none' : '1px solid var(--border)',
              transition: 'background 0.1s, color 0.1s',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => {
              if (!active) {
                e.currentTarget.style.background = 'var(--surface2)';
                e.currentTarget.style.color = 'var(--text)';
              }
            }}
            onMouseLeave={e => {
              if (!active) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--text-dim)';
              }
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * RemindersSection — the Reminders controls inside the Settings modal.
 *
 * The list-designation action lives on each list's ⋯ menu now (cleaner
 * than a settings-level picker, since the bell indicators in the
 * sidebar already show what's designated). This section just renders
 * the master toggle, delivery prefs, and a one-line summary of which
 * lists are currently designated.
 */
function RemindersSection({ userPrefs, updateUserPrefs, lists, currentBoardId, allBoards, pushSub }) {
  // Fallback hint for when the OS push permission prompt was blocked
  // (denied) — we auto-flip email on instead and surface this banner
  // so the user knows what happened. Pre-prompt removed: the OS
  // prompt itself is the confirmation step.
  const [pushFallbackHint, setPushFallbackHint] = useState(null);
  return (
    <>
      <SectionHeader>
        reminders <span style={{
          fontSize: 'calc(9px * var(--ui-scale, 1))',
          color: 'var(--text-dimmer)',
          marginLeft: '6px',
          letterSpacing: '0.6px',
          fontWeight: 400,
        }}>early beta</span>
      </SectionHeader>

      {/* Walter 2026-05-06 round 8: master on/off toggle removed.
          If the user doesn't want reminders, they don't add 🔔 lines
          (or use a designated list). The capture popover only fires
          on lines that look like reminders, so leaving the feature
          on costs nothing for users who never trigger it. The
          explainer text that lived in the !enabled branch is now
          the section intro for everyone. */}
      <div style={{
        margin: '0 0 14px',
        padding: '8px 10px',
        background: 'var(--surface2)',
        border: '1px dashed var(--border)',
        borderRadius: 'var(--radius)',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(10px * var(--ui-scale, 1))',
        color: 'var(--text-dimmer)',
        lineHeight: 1.5,
        maxWidth: '360px',
      }}>
        Start a line with <span style={{color:'var(--accent)'}}>reminder:</span> or
        {' '}<span style={{color:'var(--accent)'}}>todo:</span> and listicity turns it into
        a tracked reminder — it reads dates like "tomorrow 3pm" automatically, and an undo
        toast lets you back out. Or designate a whole list as a reminder list from its
        {' '}<span style={{ color: 'var(--text-dim)', fontWeight: 600 }}>⋯ menu</span>, and
        every line in it becomes a reminder. Lines directly under a reminder are notes
        on it — leave a blank line to start the next one.
      </div>

      <div>

      {/* Delivery channels — push (browser) and email. Both default
          per useUserPrefs DEFAULTS (push on, email off). The actual
          delivery firing happens in Cloud Functions later in v1; the
          toggles persist now so users can opt in/out before that
          ships. */}
      {/* Default time for date-only reminders. When the user types
          "tomorrow call mom" (date but no explicit time), we splice
          this hour into both the line text and the trigger Date. */}
      <Row label="default time">
        <input
          type="time"
          value={userPrefs?.defaultReminderTime || '07:00'}
          onChange={(e) => updateUserPrefs({ defaultReminderTime: e.target.value })}
          style={{
            background: 'var(--surface)', color: 'var(--text)',
            border: '1px solid var(--border-bright)',
            borderRadius: 'var(--radius)',
            padding: '3px 6px',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(11px * var(--ui-scale, 1))',
            colorScheme: 'dark',
          }}
        />
      </Row>
      <Row label={<RowLabelWithTip text="push notifications" tip={PUSH_TIP} />}>
        <Segmented
          options={[
            { label: 'on',  value: true },
            { label: 'off', value: false },
          ]}
          value={!!userPrefs?.deliverPush && !!pushSub?.subscribed}
          onChange={async (v) => {
            // Direct flip — no pre-prompt. The OS permission dialog
            // is itself a confirmation, and an extra click in front
            // of it just adds friction. If the OS prompt is denied
            // we still fall back to enabling email below.
            if (v) {
              setPushFallbackHint(null);
              if (!pushSub?.subscribe) return;
              updateUserPrefs({ deliverPush: true });
              const ok = await pushSub.subscribe();
              if (!ok) {
                // Permission denied or unsupported — flip the pref
                // back AND opt the user into email as a fallback,
                // so we still have a way to reach them when a
                // reminder fires. Surface the fallback inline so
                // the user knows what just happened.
                updateUserPrefs({ deliverPush: false, deliverEmail: true });
                setPushFallbackHint(
                  "Push wasn't enabled — we turned email reminders on instead so you'll still get pinged."
                );
              }
            } else {
              updateUserPrefs({ deliverPush: false });
              if (pushSub?.unsubscribe) await pushSub.unsubscribe();
            }
          }}
        />
      </Row>
      {pushFallbackHint && (
        <div style={{
          margin: '0 0 14px',
          padding: '6px 10px',
          background: 'var(--surface2)',
          border: '1px dashed var(--accent-dim)',
          borderRadius: 'var(--radius)',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          maxWidth: '360px',
        }}>
          {pushFallbackHint}
        </div>
      )}
      {pushSub?.error && (
        <div style={{
          margin: '0 0 14px',
          padding: '6px 10px',
          background: 'var(--surface2)',
          border: '1px solid var(--danger, #d04a4a)',
          borderRadius: 'var(--radius)',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--danger, #d04a4a)',
          maxWidth: '360px',
        }}>
          {pushSub.error}
        </div>
      )}
      {pushSub?.permission === 'denied' && (
        <div style={{
          margin: '0 0 14px',
          padding: '8px 10px',
          background: 'var(--surface2)',
          border: '1px dashed var(--border)',
          borderRadius: 'var(--radius)',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dimmer)',
          maxWidth: '360px',
          lineHeight: 1.55,
        }}>
          Notifications are blocked for this site. To re-enable:
          <ol style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
            <li>Click the lock icon in the address bar.</li>
            <li>Set <strong>Notifications</strong> to <strong>Allow</strong>.</li>
            <li>Reload the page, then toggle push back on.</li>
          </ol>
          <div style={{ marginTop: '6px', color: 'var(--text-dim)' }}>
            (Email reminders work in the meantime — toggle them on below.)
          </div>
        </div>
      )}
      <Row label={<RowLabelWithTip text="email notifications" tip={EMAIL_TIP} />}>
        <Segmented
          options={[
            { label: 'on',  value: true },
            { label: 'off', value: false },
          ]}
          value={!!userPrefs?.deliverEmail}
          onChange={(v) => updateUserPrefs({ deliverEmail: !!v })}
        />
      </Row>

      {/* Reminder digests — Walter 2026-05-14. Two opt-in summary
          emails: daily (today + overdue + undated) at user-picked
          hour, weekly (next 7 days + overdue + undated) at user-
          picked day + hour. Both ADDITIVE — per-reminder emails
          continue to fire. Time/day inputs only render when the
          toggle is on, to keep Settings tidy when both are off. */}
      <Row label={<RowLabelWithTip text="daily digest" tip={DIGEST_TIP} />}>
        <Segmented
          options={[
            { label: 'on',  value: true },
            { label: 'off', value: false },
          ]}
          value={!!userPrefs?.digestDailyEnabled}
          onChange={(v) => updateUserPrefs({ digestDailyEnabled: !!v })}
        />
      </Row>
      {!!userPrefs?.digestDailyEnabled && (
        <Row label="daily at">
          <input
            type="time"
            value={hourToStr(userPrefs?.digestDailyHour ?? 7)}
            onChange={(e) => updateUserPrefs({ digestDailyHour: strToHour(e.target.value) })}
            style={{
              background: 'var(--surface)', color: 'var(--text)',
              border: '1px solid var(--border-bright)',
              borderRadius: 'var(--radius)',
              padding: '3px 6px',
              fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
            }}
          />
        </Row>
      )}

      <Row label="weekly digest">
        <Segmented
          options={[
            { label: 'on',  value: true },
            { label: 'off', value: false },
          ]}
          value={!!userPrefs?.digestWeeklyEnabled}
          onChange={(v) => updateUserPrefs({ digestWeeklyEnabled: !!v })}
        />
      </Row>
      {!!userPrefs?.digestWeeklyEnabled && (
        <Row label="weekly on">
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <select
              value={userPrefs?.digestWeeklyDayOfWeek ?? 1}
              onChange={(e) => updateUserPrefs({ digestWeeklyDayOfWeek: parseInt(e.target.value, 10) })}
              style={{
                background: 'var(--surface)', color: 'var(--text)',
                border: '1px solid var(--border-bright)',
                borderRadius: 'var(--radius)',
                padding: '3px 6px',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
              }}
            >
              {DAY_NAMES.map((name, idx) => (
                <option key={idx} value={idx}>{name}</option>
              ))}
            </select>
            <span style={{ color: 'var(--text-dim)', fontSize: 'calc(10px * var(--ui-scale, 1))' }}>at</span>
            <input
              type="time"
              value={hourToStr(userPrefs?.digestWeeklyHour ?? 8)}
              onChange={(e) => updateUserPrefs({ digestWeeklyHour: strToHour(e.target.value) })}
              style={{
                background: 'var(--surface)', color: 'var(--text)',
                border: '1px solid var(--border-bright)',
                borderRadius: 'var(--radius)',
                padding: '3px 6px',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
              }}
            />
          </div>
        </Row>
      )}

      {/* Quiet hours — UI removed. Walter's call: a default fire-time
          (7am for non-timed) is enough; if a user really sets a 3am
          reminder, they probably want it. The pref still exists in
          Firestore so a future re-introduction is just un-hiding
          this row, no migration. The Cloud Function still honours
          `quietHoursEnabled` if it's set to true via direct doc edit. */}
      {false && (
      <Row label="quiet hours">
        <Segmented
          options={[
            { label: 'on',  value: true },
            { label: 'off', value: false },
          ]}
          value={!!userPrefs?.quietHoursEnabled}
          onChange={(v) => updateUserPrefs({ quietHoursEnabled: !!v })}
        />
      </Row>
      )}
      {!!userPrefs?.quietHoursEnabled && (
        <Row label="quiet from / to">
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <input
              type="time"
              value={userPrefs.quietHoursStart || '22:00'}
              onChange={(e) => updateUserPrefs({ quietHoursStart: e.target.value })}
              style={{
                background: 'var(--surface)', color: 'var(--text)',
                border: '1px solid var(--border-bright)',
                borderRadius: 'var(--radius)',
                padding: '3px 6px',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                colorScheme: 'dark',
              }}
            />
            <span style={{
              fontFamily: 'var(--mono)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              color: 'var(--text-dimmer)',
            }}>→</span>
            <input
              type="time"
              value={userPrefs.quietHoursEnd || '07:00'}
              onChange={(e) => updateUserPrefs({ quietHoursEnd: e.target.value })}
              style={{
                background: 'var(--surface)', color: 'var(--text)',
                border: '1px solid var(--border-bright)',
                borderRadius: 'var(--radius)',
                padding: '3px 6px',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                colorScheme: 'dark',
              }}
            />
          </div>
        </Row>
      )}
      </div>
    </>
  );
}

// SharingSection removed Walter 2026-05-06 round 7 — sharing is no
// longer alpha-toggled; the section + toggle that lived here have
// been deleted from settings entirely.

/**
 * RowLabelWithTip — drop-in label that pairs a Row's text with an
 * InfoTip glyph. Walter 2026-05-09: kept tiny so the existing Row
 * helper doesn't need to change shape; Row already accepts a ReactNode
 * for `label`, so we just hand it this flex span.
 */
function RowLabelWithTip({ text, tip }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {text}
      <InfoTip label={`about ${text}`}>{tip}</InfoTip>
    </span>
  );
}

/**
 * InstallSection — always-available install entry inside Settings,
 * paired with the InstallHint banner. Renders one of three shapes
 * depending on the device / browser state (driven by useInstallPrompt):
 *
 *   - Chrome/Edge/Android with a deferred prompt: "install" button.
 *   - iOS Safari (no installation API): inline Add-to-Home-Screen
 *     instructions so users don't have to read the banner copy from
 *     memory after dismissing it.
 *   - Already running standalone, or browser doesn't support PWA
 *     install: renders nothing (no action available).
 *
 * Walter 2026-05-15: added so dismissing the auto-banner doesn't
 * lose the user — they can find install here forever.
 */
function InstallSection() {
  const { canInstall, isIOS, isStandalone, installed, prompt } = useInstallPrompt();
  const [busy, setBusy] = useState(false);
  const [postPromptOutcome, setPostPromptOutcome] = useState(null);

  // No-op only when already running standalone — there's genuinely
  // nothing to install then.
  if (isStandalone || installed) return null;
  // Walter 2026-05-31: previously also returned null when neither a
  // deferred prompt nor iOS was detected — but that's exactly the case
  // that stranded users (Marco): no prompt fired, so Settings showed
  // nothing, so install was unreachable. Now we ALWAYS render the
  // section when not standalone, falling back to manual browser-menu
  // instructions when no programmatic prompt is available. This also
  // makes the onboarding checklist's "find it in Settings later"
  // pointer truthful.

  const onInstall = async () => {
    setBusy(true);
    setPostPromptOutcome(null);
    const choice = await prompt();
    setBusy(false);
    setPostPromptOutcome(choice?.outcome || 'unavailable');
  };

  return (
    <div style={{ marginTop: '14px' }}>
      <SectionHeader>install app</SectionHeader>
      {isIOS ? (
        // iOS — show the manual Add-to-Home-Screen instructions.
        <div style={{
          fontSize: 'calc(11px * var(--ui-scale, 1))',
          color: 'var(--text-dim)',
          lineHeight: 1.55,
          marginBottom: '14px',
        }}>
          On iPhone / iPad: tap the{' '}
          <strong style={{ color: 'var(--text)' }}>Share</strong> button{' '}
          <span style={{ color: 'var(--accent)' }}>↑</span> in Safari, then{' '}
          <strong style={{ color: 'var(--text)' }}>Add to Home Screen</strong>.
          Open listicity from the home-screen icon to enable push
          notifications.
        </div>
      ) : (
        // Chrome / Edge / Android with a deferred prompt available.
        <div style={{ marginBottom: '14px' }}>
          <div style={{
            fontSize: 'calc(11px * var(--ui-scale, 1))',
            color: 'var(--text-dim)',
            lineHeight: 1.55,
            marginBottom: '10px',
          }}>
            Run listicity as a standalone app — quicker launch from your
            dock, offline access, and push reminders. Same app, no app
            store.
          </div>
          {canInstall ? (
            <button
              onClick={onInstall}
              disabled={busy}
              style={{
                padding: '7px 16px',
                border: '1px solid var(--accent)',
                background: 'var(--accent)',
                color: 'var(--bg)',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                fontFamily: 'var(--mono)',
                fontSize: 'calc(11px * var(--ui-scale, 1))',
                fontWeight: 600,
                opacity: busy ? 0.7 : 1,
                transition: 'opacity 0.12s',
              }}
            >
              {busy ? 'installing…' : 'install'}
            </button>
          ) : (
            // No deferred prompt fired (Firefox, or Chrome hasn't
            // decided we're eligible). Rather than a dead disabled
            // button, give the manual browser-menu path so install is
            // always reachable from Settings — this is what the
            // onboarding checklist points users here for.
            <div style={{
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              color: 'var(--text-dim)',
              lineHeight: 1.55,
            }}>
              To install, open your browser menu{' '}
              <strong style={{ color: 'var(--text)' }}>(⋮)</strong> and choose{' '}
              <strong style={{ color: 'var(--text)' }}>Install listicity</strong>{' '}
              or <strong style={{ color: 'var(--text)' }}>Add to Home Screen</strong>.
              If you don&rsquo;t see it, your browser may not support
              installing — listicity still works in a normal tab.
            </div>
          )}
          {postPromptOutcome === 'accepted' && (
            <div style={{
              marginTop: '8px',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              color: 'var(--accent)',
            }}>
              ✓ installed — look in your dock / start menu / home screen.
            </div>
          )}
          {postPromptOutcome === 'dismissed' && (
            <div style={{
              marginTop: '8px',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              color: 'var(--text-dim)',
            }}>
              Install was cancelled. Refresh this page if you change
              your mind — your browser will offer to install again.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
